/**
 * BulkApplyModal - Apply landing page settings to multiple cards
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';

export const BulkApplyModal = ({
    open, onOpenChange,
    bulkApplyOrg, setBulkApplyOrg,
    bulkApplyLocation, setBulkApplyLocation,
    bulkApplying, handleBulkApply,
    organizations, locations
}) => {
    const { t } = useTranslation();
    // Memoize the filtered location list so it does not recompute on every
    // render — it only depends on `locations` and `bulkApplyOrg`.
    const scopedLocations = useMemo(
        () => locations.filter((loc) => !bulkApplyOrg || loc.organization_id === bulkApplyOrg),
        [locations, bulkApplyOrg],
    );
    return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
            <DialogHeader>
                <DialogTitle>{t('bulkApply.title')}</DialogTitle>
                <DialogDescription>
                    {t(
                        'bulkApply.description',
                        'Apply ALL of this card\'s settings — including Interaction Mode, Review Shield + Global Review settings, wallpapers, logos, language preferences, and notification recipients — to every card in the selected organization and/or location.'
                    )}
                </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
                <div>
                    <Label className="mb-2 block">Organization</Label>
                    <Select value={bulkApplyOrg || "all"} onValueChange={(v) => setBulkApplyOrg(v === "all" ? "" : v)}>
                        <SelectTrigger><SelectValue placeholder={t('roiDashboard.allOrganizations')} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Organizations</SelectItem>
                            {organizations.map(org => (
                                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="mb-2 block">Location</Label>
                    <Select value={bulkApplyLocation || "all"} onValueChange={(v) => setBulkApplyLocation(v === "all" ? "" : v)} disabled={!bulkApplyOrg}>
                        <SelectTrigger><SelectValue placeholder={bulkApplyOrg ? "Select Location" : "Select Organization first"} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Locations</SelectItem>
                            {scopedLocations.map((loc) => (
                                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                        {t(
                            'bulkApply.overwriteWarning',
                            'This will overwrite ALL existing card settings (including wallpapers, logos, and Global Review settings) on every matching card. There is no undo.'
                        )}
                    </p>
                </div>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button onClick={handleBulkApply} disabled={bulkApplying || (!bulkApplyOrg && !bulkApplyLocation)} data-testid="confirm-bulk-apply-btn">
                    {bulkApplying ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Applying...</>) : ('Apply to Cards')}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
    );
};
